import React from 'react';
import { Box, Card, Grid, Typography, Chip, useTheme, useMediaQuery } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Assessment as AssessmentIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import { ResumenGeneral } from '../../../types/consolidatedReport.types';
import CountUp from 'react-countup';

interface ReportSummaryCardsProps {
  resumen: ResumenGeneral;
}

const ReportSummaryCards: React.FC<ReportSummaryCardsProps> = ({ resumen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { metricas } = resumen;

  const getTrendColor = () => {
    switch (metricas.tendencia.direccion) {
      case 'up': return 'success';
      case 'down': return 'error';
      case 'sin_datos': return 'default';
      default: return 'info';
    }
  };

  const getTrendIcon = () => {
    const iconSize = isMobile ? 20 : 24;
    switch (metricas.tendencia.direccion) {
      case 'up': return <TrendingUpIcon sx={{ fontSize: iconSize }} />;
      case 'down': return <TrendingDownIcon sx={{ fontSize: iconSize }} />;
      default: return <TrendingFlatIcon sx={{ fontSize: iconSize }} />;
    }
  };

  const formatPeriodoAnterior = () => {
    const p = metricas.tendencia.periodo_anterior;
    if (!p) return '';
    const fmt = (d: string) => {
      const [y, m] = d.split('-');
      return new Date(parseInt(y), parseInt(m) - 1).toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
    };
    return `${fmt(p.desde)} – ${fmt(p.hasta)}`;
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Primera fila: Cumplimiento General y Total Auditorías */}
      <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 1 : 2 }}>
        {/* Cumplimiento General */}
        <Grid item xs={6}>
          <Card
            sx={{
              p: isMobile ? 2 : 3,
              height: isMobile ? 140 : 160,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', width: '100%' }}>
              <Typography variant="caption" sx={{ mb: 1, opacity: 0.9, fontSize: isMobile ? 10 : 12, display: 'block' }}>
                Cumplimiento General
              </Typography>
              <Typography variant={isMobile ? 'h3' : 'h2'} sx={{ fontWeight: 700, mb: 0.5 }}>
                <CountUp
                  end={metricas.cumplimiento_general}
                  duration={1.5}
                  decimals={1}
                  decimal="."
                />%
              </Typography>
              <Box
                sx={{
                  width: '80%',
                  height: isMobile ? 4 : 6,
                  bgcolor: 'rgba(255,255,255,0.3)',
                  borderRadius: 4,
                  overflow: 'hidden',
                  mt: 1,
                  mx: 'auto'
                }}
              >
                <Box
                  sx={{
                    width: `${metricas.cumplimiento_general}%`,
                    height: '100%',
                    bgcolor: 'white',
                    borderRadius: 4,
                    transition: 'width 1.5s ease'
                  }}
                />
              </Box>
            </Box>
            <AssessmentIcon sx={{ 
              position: 'absolute', 
              right: -10, 
              bottom: -10, 
              opacity: 0.1, 
              fontSize: isMobile ? 60 : 80,
              transform: 'rotate(-15deg)' 
            }} />
          </Card>
        </Grid>

        {/* Total Auditorías */}
        <Grid item xs={6}>
          <Card
            sx={{
              p: isMobile ? 2 : 3,
              height: isMobile ? 140 : 160,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white'
            }}
          >
            <Typography variant="caption" sx={{ mb: 1, opacity: 0.9, fontSize: isMobile ? 10 : 12 }}>
              Total Auditorías
            </Typography>
            <Typography variant={isMobile ? 'h3' : 'h2'} sx={{ fontWeight: 700 }}>
              <CountUp end={metricas.total_auditorias} duration={1.5} />
            </Typography>
            <Typography variant="caption" sx={{ mt: 1, opacity: 0.9, fontSize: isMobile ? 9 : 11 }}>
              {metricas.distribucion_temporal.periodos}{' '}
              {metricas.distribucion_temporal.tipo === 'meses' ? 'meses' : 'semanas'}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Segunda fila: Tendencia y Distribución Temporal */}
      <Grid container spacing={isMobile ? 1 : 2}>
        {/* Tendencia */}
        <Grid item xs={6}>
          <Card sx={{ 
            p: isMobile ? 2 : 3, 
            height: isMobile ? 140 : 160,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, fontSize: isMobile ? 10 : 12, textAlign: 'center' }}>
              Tendencia vs Anterior
            </Typography>
            {metricas.tendencia.direccion === 'sin_datos' ? (
              <>
                <TrendingFlatIcon sx={{ fontSize: isMobile ? 28 : 36, color: 'text.disabled', mb: 0.5 }} />
                <Chip label="Sin datos" color="default" size="small" sx={{ fontSize: isMobile ? 9 : 11 }} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontSize: isMobile ? 9 : 10, textAlign: 'center' }}>
                  No hay auditorías en{isMobile ? '\n' : ' '}el período anterior
                </Typography>
                {metricas.tendencia.periodo_anterior && (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: isMobile ? 8 : 10, textAlign: 'center' }}>
                    ({formatPeriodoAnterior()})
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {getTrendIcon()}
                  <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 700 }}>
                    {(metricas.tendencia.valor ?? 0) > 0 ? '+' : ''}
                    {metricas.tendencia.valor}%
                  </Typography>
                </Box>
                <Chip
                  label={
                    metricas.tendencia.direccion === 'up' ? 'Mejora'
                    : metricas.tendencia.direccion === 'down' ? 'Declive'
                    : 'Estable'
                  }
                  color={getTrendColor() as any}
                  size="small"
                  sx={{ fontSize: isMobile ? 9 : 11 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontSize: isMobile ? 9 : 11, textAlign: 'center' }}>
                  Anterior: {metricas.tendencia.promedio_anterior?.toFixed(1)}%
                </Typography>
                {metricas.tendencia.periodo_anterior && (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: isMobile ? 8 : 10, textAlign: 'center' }}>
                    ({formatPeriodoAnterior()})
                  </Typography>
                )}
              </>
            )}
          </Card>
        </Grid>

        {/* Distribución Temporal */}
        <Grid item xs={6}>
          <Card sx={{ 
            p: isMobile ? 2 : 3, 
            height: isMobile ? 140 : 160,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, fontSize: isMobile ? 10 : 12, textAlign: 'center' }}>
              Distribución Temporal
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DateRangeIcon sx={{ fontSize: isMobile ? 32 : 40, color: 'primary.main' }} />
              <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 700 }}>
                {metricas.distribucion_temporal.periodos}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 9 : 11 }}>
              {metricas.distribucion_temporal.tipo === 'meses' ? 'Meses' : 'Semanas'}
            </Typography>
            {metricas.total_auditorias > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontSize: isMobile ? 9 : 11, textAlign: 'center' }}>
                Prom: {(metricas.total_auditorias / metricas.distribucion_temporal.periodos).toFixed(1)}/
                {metricas.distribucion_temporal.tipo === 'meses' ? 'mes' : 'sem'}
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportSummaryCards;
